/**
 * ACTIVITY-CORE (L3) — "Activity" page. Presentation only.
 *
 * PLUGIN-KERNEL: the data half (local mirror query, deep-history paging, the
 * merge between them and the teardown on account switch) lives in the activity
 * plugin (`@listto/shared/solid`), mounted by `apps/web/src/kernel/composition.ts`.
 * This component reads the published service and renders it.
 */
import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { History } from "@fleet/icons";
import { ACTIVITY_FEED } from "@fleet/plugins/activity";
import { useKernelService } from "@fleet/kernel";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { ActivityList } from "./activity-list";

export function ActivityFeedPage() {
  const { t } = useI18n();
  const { userId } = useSession();
  // `undefined` when the plugin was not mounted here — e.g. a target without a
  // local database. The empty state below is the honest answer, not a crash.
  const feed = useKernelService(ACTIVITY_FEED);

  return (
    <div class="space-y-6" data-testid="activity-page">
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <History class="h-5 w-5" />
        </div>
        <div>
          <h1 class="text-2xl font-bold tracking-tight">{t(msg`Activity`)}</h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Who did what across your tasks, lists and shared spaces.`)}
          </p>
        </div>
      </div>

      <ActivityList
        rows={() => feed()?.rows() ?? []}
        isLoading={() => feed()?.isLoading() ?? true}
        grouped
        currentUserId={userId}
        canRevert
      />

      <Show when={feed()?.hasMore()}>
        <div class="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            disabled={feed()?.isLoadingOlder()}
            onClick={() => void feed()?.loadOlder()}
            data-testid="activity-load-older"
          >
            {feed()?.isLoadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
